import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { publicAPI } from "@/services/api";
import { BookOpen, Mail, MessageCircle, Send, Sparkles, Users, Phone } from "lucide-react";

const FAQ_ITEMS = [
  {
    question: "Comment devenir partenaire MMJ ?",
    answer: "L'inscription se fait depuis la page dédiée. Après validation de vos informations, vous accédez à votre tableau de bord partenaire et aux fonctionnalités liées à votre statut.",
  },
  {
    question: "Puis-je consulter le catalogue sans créer de compte ?",
    answer: "Oui. Le catalogue public reste accessible pour découvrir les ouvrages avant toute inscription.",
  },
  {
    question: "Comment poser une question à l'équipe Artico-MMJ ?",
    answer: "Utilisez le formulaire de contact ci-dessus. Le message est envoyé à l'adresse Artico configurée par l'administration.",
  },
  {
    question: "Le numéro WhatsApp affiché change-t-il automatiquement ?",
    answer: "Oui. La bulle WhatsApp lit le numéro défini par l'administration dans les réglages de la plateforme.",
  },
  {
    question: "Les achats et bonus sont-ils visibles après connexion ?",
    answer: "Oui. Le tableau de bord partenaire centralise les achats, bonus, notifications et archives de votre activité.",
  },
];

const FALLBACK_SETTINGS = {
  support_email: "artico@mymmj.app",
  official_email: "artico@mymmj.app",
  whatsapp_phone: "+243 XXX XXX XXX",
  support_phone: "+243 XXX XXX XXX",
};

export default function PublicContactFaqSection({
  className = "",
  title = "Questions fréquentes",
  intro = "Les réponses rapides aux points qui reviennent le plus souvent avant une inscription ou une prise de contact.",
  sectionLabel = "FAQ",
  compact = false,
  platformSettings = null,
}) {
  const [remoteSettings, setRemoteSettings] = useState(FALLBACK_SETTINGS);
  const [contactForm, setContactForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [contactStatus, setContactStatus] = useState({ type: "", message: "" });
  const [contactLoading, setContactLoading] = useState(false);

  useEffect(() => {
    if (platformSettings) {
      setRemoteSettings((current) => ({ ...current, ...platformSettings }));
      return undefined;
    }

    let alive = true;
    publicAPI.getPlatformSettings()
      .then((res) => {
        if (!alive) return;
        setRemoteSettings((current) => ({ ...current, ...(res.data || {}) }));
      })
      .catch(() => {});

    return () => {
      alive = false;
    };
  }, [platformSettings]);

  const settings = useMemo(
    () => ({ ...FALLBACK_SETTINGS, ...remoteSettings, ...(platformSettings || {}) }),
    [platformSettings, remoteSettings]
  );

  const supportEmail = settings.support_email || settings.official_email;
  const stackedLayout = compact;

  const handleContactChange = (field) => (event) => {
    const value = event.target.value;
    setContactForm((current) => ({ ...current, [field]: value }));
  };

  const handleContactSubmit = async (event) => {
    event.preventDefault();
    setContactLoading(true);
    setContactStatus({ type: "", message: "" });

    try {
      const response = await publicAPI.submitContactInquiry(contactForm);
      setContactStatus({
        type: "success",
        message: response.data?.message || "Votre question a ete transmise a Artico-MMJ.",
      });
      setContactForm({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        message: "",
      });
    } catch (error) {
      setContactStatus({
        type: "error",
        message: error.response?.data?.detail || "Impossible d'envoyer votre message pour le moment.",
      });
    } finally {
      setContactLoading(false);
    }
  };

  return (
    <section className={className}>
      <div className={`mx-auto w-full max-w-6xl px-4 sm:px-6 ${compact ? "py-8 sm:py-10" : "py-12 sm:py-16 lg:py-20"}`}>
        {stackedLayout ? (
          <div className="space-y-6 sm:space-y-7">
            <ContactPanel
              supportEmail={supportEmail}
              contactForm={contactForm}
              contactLoading={contactLoading}
              contactStatus={contactStatus}
              handleContactChange={handleContactChange}
              handleContactSubmit={handleContactSubmit}
              compact={compact}
            />
            <FaqPanel title={title} intro={intro} sectionLabel={sectionLabel} compact={compact} />
          </div>
        ) : (
          <div className="grid gap-8 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:gap-10">
            <FaqPanel title={title} intro={intro} sectionLabel={sectionLabel} compact={compact} />
            <ContactPanel
              supportEmail={supportEmail}
              contactForm={contactForm}
              contactLoading={contactLoading}
              contactStatus={contactStatus}
              handleContactChange={handleContactChange}
              handleContactSubmit={handleContactSubmit}
              compact={compact}
            />
          </div>
        )}
      </div>
    </section>
  );
}

function FaqPanel({ title, intro, sectionLabel, compact }) {
  return (
    <div>
      <div className="inline-flex items-center gap-2 rounded-full border border-[#E6E4E0] bg-white px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#2A4B46] sm:text-xs">
        <Sparkles size={14} />
        {sectionLabel}
      </div>
      <h3 className={`font-['Outfit'] font-bold text-[#2A4B46] ${compact ? "mt-4 text-xl sm:text-2xl" : "mt-5 text-2xl sm:text-3xl"}`}>
        {title}
      </h3>
      <p className={`max-w-2xl text-sm text-[#5C625F] ${compact ? "mt-2 leading-6 sm:mt-3 sm:text-base sm:leading-7" : "mt-3 leading-7 sm:text-base"}`}>
        {intro}
      </p>

      <div className={compact ? "mt-6 space-y-3" : "mt-8 space-y-3"}>
        {FAQ_ITEMS.map((item) => (
          <details key={item.question} className={`group rounded-2xl border border-[#E6E4E0] bg-white shadow-sm open:shadow-md ${compact ? "px-4 py-3.5" : "px-5 py-4"}`}>
            <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-left text-sm font-semibold text-[#2A4B46] sm:text-base">
              <span>{item.question}</span>
              <span className="text-[#D4A373] transition-transform duration-300 group-open:rotate-45">+</span>
            </summary>
            <p className={`text-sm text-[#5C625F] ${compact ? "mt-2.5 leading-6" : "mt-3 leading-7"}`}>
              {item.answer}
            </p>
          </details>
        ))}
      </div>
    </div>
  );
}

function ContactPanel({
  supportEmail,
  contactForm,
  contactLoading,
  contactStatus,
  handleContactChange,
  handleContactSubmit,
  compact,
}) {
  return (
    <div className="rounded-[1.5rem] border border-[#E6E4E0] bg-white p-4 shadow-sm sm:p-6">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#2A4B46] text-white">
          <Mail size={18} />
        </div>
        <div>
          <h3 className="text-lg font-bold text-[#2A4B46] font-['Outfit'] sm:text-xl">
            Poser une question
          </h3>
          <p className="text-xs text-[#5C625F] sm:text-sm">
            Votre message part vers {supportEmail} et reste traçable côté Artico-MMJ.
          </p>
        </div>
      </div>

      <form className="mt-5 space-y-3.5 sm:mt-6 sm:space-y-4" onSubmit={handleContactSubmit}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Prénom" icon={Users}>
            <Input value={contactForm.first_name} onChange={handleContactChange("first_name")} required minLength={2} placeholder="Prénom" />
          </Field>
          <Field label="Nom" icon={Users}>
            <Input value={contactForm.last_name} onChange={handleContactChange("last_name")} required minLength={2} placeholder="Nom" />
          </Field>
        </div>
        <Field label="E-mail" icon={Mail}>
          <Input type="email" value={contactForm.email} onChange={handleContactChange("email")} required placeholder="votre@email.com" />
        </Field>
        <Field label="Téléphone" icon={Phone}>
          <Input value={contactForm.phone} onChange={handleContactChange("phone")} required placeholder="Votre numéro de téléphone ici" />
        </Field>
        <Field label="Votre question" icon={MessageCircle}>
          <Textarea
            value={contactForm.message}
            onChange={handleContactChange("message")}
            required
            minLength={10}
            rows={compact ? 4 : 6}
            placeholder="Écrivez ici votre question pour les admins Artico-MMJ."
          />
        </Field>

        {contactStatus.message ? (
          <p className={`rounded-xl px-4 py-3 text-sm ${contactStatus.type === "success" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
            {contactStatus.message}
          </p>
        ) : null}

        <Button
          type="submit"
          className="h-11 w-full rounded-full bg-[#2A4B46] text-white hover:bg-[#1f3a36]"
          disabled={contactLoading}
        >
          {contactLoading ? "Envoi en cours..." : "Envoyer aux admins"}
          <Send className="ml-2" size={16} />
        </Button>
      </form>
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <label className="block">
      <span className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#7A6250]">
        <Icon size={14} />
        {label}
      </span>
      {children}
    </label>
  );
}
